import type { BackupItem, BackupStatus, EntryChecklistAnswers, ChecklistAnswers, MaintenanceVisibility, OrderStatus, PaymentMethod, PaymentStatus, ServiceType, SignatureKind } from '@oficina/shared';
import { api } from '@/lib/api';
import { normalizeImages } from '@/lib/image-normalize';
import type {
  MaintenanceEntry,
  OrderEntryChecklistData,
  OrderChecklistData,
  OrderDetail,
  OrderPhoto,
  OrderPrintData,
  OrderSignatureState,
  PublicSignatureData,
  PortalData,
  OrderSecrets,
  OrderSummary,
  OriginCandidate,
  Paginated,
  Payment,
} from '@/types/api';

export interface OrderListParams {
  search?: string;
  status?: OrderStatus | '';
  hideCompleted?: boolean;
  clientId?: string;
  equipmentId?: string;
  page?: number;
  pageSize?: number;
  sort?: 'recent' | 'oldest' | 'number';
}

export interface OrderItemPayload {
  type: 'PRODUCT' | 'SERVICE';
  productId?: string | null;
  serviceId?: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  warrantyDays: number;
}

export interface OrderPayload {
  clientId: string;
  equipmentId?: string;
  technicianId?: string | null;
  reportedProblem: string;
  technicalDiagnosis?: string | null;
  physicalCondition?: string | null;
  accessoriesReceived?: string | null;
  notes?: string | null;
  entryDate: string;
  entryTime?: string | null;
  devicePassword?: string | null;
  unlockPattern?: number[] | null;
  items: OrderItemPayload[];
  initialMaintenance?: string[];
  serviceType: ServiceType;
  relatedOrderId: string | null;
  warrantyInvoiceNumber: string | null;
  warrantyPurchaseDate: string | null;
  warrantyStore: string | null;
  warrantyManufacturer: string | null;
  backupRequired: boolean | null;
  backupStatus: BackupStatus | null;
  backupItems: BackupItem[];
  backupNotes: string | null;
}

type PaymentResult = { payment: Payment; paymentStatus: PaymentStatus; paidAmount: number };

export const ordersService = {
  list: (params: OrderListParams, signal?: AbortSignal) =>
    api.get<Paginated<OrderSummary>>('/service-orders', { ...params }, signal),
  bench: (signal?: AbortSignal) => api.get<OrderSummary[]>('/service-orders/bench', undefined, signal),
  get: (id: string) => api.get<OrderDetail>(`/service-orders/${id}`),
  create: (payload: OrderPayload) => api.post<OrderDetail>('/service-orders', payload),
  update: (id: string, payload: Partial<OrderPayload>) => api.patch<OrderDetail>(`/service-orders/${id}`, payload),
  remove: (id: string) => api.delete<{ result: 'deleted' }>(`/service-orders/${id}`),
  originCandidates: (params: { clientId: string; equipmentId?: string; excludeOrderId?: string; search?: string }) =>
    api.get<OriginCandidate[]>('/service-orders/origin-candidates', params),
  changeStatus: (id: string, status: OrderStatus, note?: string, deliveredAt?: string) =>
    api.post<OrderDetail>(`/service-orders/${id}/status`, { status, note: note || null, ...(deliveredAt ? { deliveredAt } : {}) }),
  setDiscount: (id: string, input: { type: 'VALUE' | 'PERCENT' | null; value: number }) =>
    api.post<OrderDetail>(`/service-orders/${id}/discount`, input),
  secrets: (id: string) => api.get<OrderSecrets>(`/service-orders/${id}/secrets`),
  print: (id: string, includeSecrets: boolean) =>
    api.get<OrderPrintData>(`/service-orders/${id}/print`, { includeSecrets }),

  addMaintenance: async (id: string, description: string, files: File[] = [], visibility: MaintenanceVisibility = 'INTERNAL') => {
    if (!files.length) return api.post<MaintenanceEntry>(`/service-orders/${id}/maintenance`, { description, visibility });
    const form = new FormData();
    form.append('description', description);
    form.append('visibility', visibility);
    for (const file of files) {
      const normalized = file.type.startsWith('image/') ? await normalizeImages([file]) : [file];
      normalized.forEach((item) => form.append('files', item));
    }
    return api.upload<MaintenanceEntry>(`/service-orders/${id}/maintenance`, form);
  },

  uploadMaintenanceAttachments: async (id: string, entryId: string, files: File[]) => {
    const form = new FormData();
    for (const file of files) {
      const normalized =
        file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name) ? await normalizeImages([file]) : [file];
      normalized.forEach((item) => form.append('files', item));
    }
    return api.upload(`/service-orders/${id}/maintenance/${entryId}/attachments`, form);
  },

  updateMaintenance: async (id: string, entryId: string, description: string, files: File[] = [], visibility?: MaintenanceVisibility) => {
    if (!files.length) return api.patch<MaintenanceEntry>(`/service-orders/${id}/maintenance/${entryId}`, { description, ...(visibility ? { visibility } : {}) });
    const form = new FormData();
    form.append('description', description);
    if (visibility) form.append('visibility', visibility);
    for (const file of files) {
      const normalized = file.type.startsWith('image/') ? await normalizeImages([file]) : [file];
      normalized.forEach((item) => form.append('files', item));
    }
    return api.patch<MaintenanceEntry>(`/service-orders/${id}/maintenance/${entryId}`, form);
  },

  removeMaintenance: (id: string, entryId: string) => api.delete<void>(`/service-orders/${id}/maintenance/${entryId}`),

  addPayment: (id: string, input: {
      amount: number;
      method: PaymentMethod;
      paidAt?: string;
      notes?: string | null;
      feeType?: 'VALUE' | 'PERCENT' | null;
      feeValue?: number | null;
    },
  ) =>
    api.post<PaymentResult>(`/service-orders/${id}/payments`, input),
  refundPayment: (id: string, paymentId: string, reason: string) =>
    api.post<PaymentResult>(`/service-orders/${id}/payments/${paymentId}/refund`, { reason }),

  uploadPhotos: async (id: string, files: File[], caption?: string, checklistItemId?: string) => {
    // Conversão roda aqui (e não em cada tela) porque toda foto do app passa por este método:
    // HEIC do iPhone viraria JPEG e imagens grandes já saem reduzidas.
    const form = new FormData();
    (await normalizeImages(files)).forEach((f) => form.append('files', f));
    if (caption) form.append('caption', caption);
    if (checklistItemId) form.append('checklistItemId', checklistItemId);
    return api.upload<OrderPhoto[]>(`/service-orders/${id}/photos`, form);
  },
  updatePhotoCaption: (id: string, photoId: string, caption: string | null) =>
    api.patch<OrderPhoto>(`/service-orders/${id}/photos/${photoId}`, { caption }),
  removePhoto: (id: string, photoId: string) => api.delete<void>(`/service-orders/${id}/photos/${photoId}`),

  signature: (id: string) => api.get<OrderSignatureState>(`/service-orders/${id}/signature`),
  sign: (id: string, input: { signerName: string; image: string; kind: SignatureKind }) =>
    api.post<OrderSignatureState>(`/service-orders/${id}/signature`, input),
  removeSignature: (id: string, kind: SignatureKind) => api.delete<void>(`/service-orders/${id}/signature`, { kind }),
  createSignatureLink: (id: string, kind: SignatureKind) =>
    api.post<{ url: string; expiresAt: string }>(`/service-orders/${id}/signature/link`, { kind }),
  publicSignature: (token: string) => api.get<PublicSignatureData>(`/public/signatures/${token}`),
  publicSign: (token: string, input: { signerName: string; image: string }) =>
    api.post<{ signed: true; number: string }>(`/public/signatures/${token}`, input),

  createPdfLink: (id: string) =>
    api.post<{ url: string; expiresAt: string }>(`/service-orders/${id}/pdf-link`),
  sendPdfEmail: (id: string, input: { to: string; subject?: string; message?: string; pdfBase64?: string }) =>
    api.post<{ success: boolean; messageId?: string; error?: string; url?: string; expiresAt?: string }>(`/service-orders/${id}/pdf-link/send-email`, input),
  publicPdfInfo: (token: string) =>
    api.get<{
      number: string;
      clientName: string;
      equipment: string;
      createdAt: string;
      deliveredAt: string | null;
      status: string;
      expiresAt: string;
    }>(`/public/pdf/${token}`),
  publicPdfDownload: (token: string) =>
    api.post<OrderPrintData>(`/public/pdf/${token}/download`),

  portalLinkStatus: (id: string) =>
    api.get<{ active: boolean; expiresAt?: string; lastViewedAt?: string | null; createdAt?: string }>(`/service-orders/${id}/portal-link`),
  createPortalLink: (id: string) => api.post<{ url: string; expiresAt: string }>(`/service-orders/${id}/portal-link`),
  revokePortalLink: (id: string) => api.delete<{ revoked: number }>(`/service-orders/${id}/portal-link`),
  publicPortal: (token: string) => api.get<PortalData>(`/public/portal/${token}`),
  publicPortalPhotos: (token: string) =>
    api.get<{ photos: { id: string; maintenanceId: string; dataUrl: string }[] }>(`/public/portal/${token}/photos`),
  publicPortalDecide: (token: string, input: { decision: 'APPROVE' | 'REJECT'; quoteHash: string; signerName: string }) =>
    api.post<{ status: string; statusLabel: string }>(`/public/portal/${token}/decision`, input),

  entryChecklist: (id: string) => api.get<OrderEntryChecklistData>(`/service-orders/${id}/entry-checklist`),
  saveEntryChecklist: (id: string, answers: EntryChecklistAnswers, refreshTemplate = false) => api.put<OrderEntryChecklistData>(`/service-orders/${id}/entry-checklist`, { answers, refreshTemplate }),
  checklist: (id: string) => api.get<OrderChecklistData>(`/service-orders/${id}/checklist`),
  saveChecklist: (id: string, answers: ChecklistAnswers, refreshTemplate = false) =>
    api.put<OrderChecklistData>(`/service-orders/${id}/checklist`, { answers, refreshTemplate }),
};
